import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Skeleton } from '@/common/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/common/ui/alert'
import { AlertTriangle } from 'lucide-react'
import { useQueryGetCurrentQuota } from '../../api/hooks/useQueryGetCurrentQuota'
import { formatNumber } from '@/common/lib/utils'

export function CurrentQuotaCard() {
	const { data, isLoading } = useQueryGetCurrentQuota()

	if (isLoading) {
		return (
			<Card>
				<CardHeader className="pb-3">
					<CardTitle className="text-lg">โควต้าปัจจุบัน</CardTitle>
				</CardHeader>
				<CardContent>
					<Skeleton className="h-24 w-full" />
				</CardContent>
			</Card>
		)
	}

	const winningQuotaRemaining =
		(data?.winningQuota ?? 0) - (data?.winningQuotaUsed ?? 0)
	const creditQuotaRemaining =
		(data?.creditQuota ?? 0) - (data?.creditQuotaUsed ?? 0)

	// Calculate percentage remaining for winning quota
	const winningQuotaTotal = data?.winningQuota ?? 0
	const winningQuotaPercentage =
		winningQuotaTotal > 0
			? (winningQuotaRemaining / winningQuotaTotal) * 100
			: 0
	const isLowWinningQuota = winningQuotaPercentage < 10 && winningQuotaTotal > 0

	return (
		<Card>
			<CardHeader className="pb-3">
				<CardTitle className="text-lg">โควต้าปัจจุบัน</CardTitle>
			</CardHeader>
			<CardContent>
				{/* Low Winning Quota Warning */}
				{isLowWinningQuota && (
					<Alert variant="destructive" className="mb-4">
						<AlertTriangle className="h-4 w-4" />
						<AlertTitle>โควตายอดได้เสียเหลือน้อย</AlertTitle>
						<AlertDescription>
							โควตายอดได้เสียของคุณเหลือน้อยกว่า 10% (
							{winningQuotaPercentage.toFixed(2)}%) กรุณาซื้อโควต้าเพิ่มเติม
						</AlertDescription>
					</Alert>
				)}

				<div className="grid gap-6 md:grid-cols-2">
					{/* Winning Quota */}
					<div className="space-y-3 rounded-lg border bg-card p-4">
						<h3 className="text-sm font-medium">
							โควตายอดได้เสีย (Winning Quota)
						</h3>

						<div className="space-y-2 text-sm">
							<div className="flex items-center justify-between">
								<span className="text-muted-foreground">โควตาทั้งหมด</span>
								<span className="font-medium">
									{formatNumber(data?.winningQuota ?? 0)}
								</span>
							</div>

							<div className="flex items-center justify-between">
								<span className="text-muted-foreground">ยอดที่ใช้แล้ว</span>
								<span className="font-medium">
									{formatNumber(data?.winningQuotaUsed ?? 0)}
								</span>
							</div>

							<div className="flex items-center justify-between border-t pt-2">
								<span className="font-semibold">คงเหลือ</span>
								<span className="text-2xl font-bold text-green-600">
									{formatNumber(winningQuotaRemaining)}
								</span>
							</div>
						</div>
					</div>

					{/* Credit Quota */}
					<div className="space-y-3 rounded-lg border bg-card p-4">
						<h3 className="text-sm font-medium">
							โควตายอดการเงิน (Credit Quota)
						</h3>

						<div className="space-y-2 text-sm">
							<div className="flex items-center justify-between">
								<span className="text-muted-foreground">โควตาทั้งหมด</span>
								<span className="font-medium">
									{formatNumber(data?.creditQuota ?? 0)}
								</span>
							</div>

							<div className="flex items-center justify-between">
								<span className="text-muted-foreground">ยอดที่ใช้แล้ว</span>
								<span className="font-medium">
									{formatNumber(data?.creditQuotaUsed ?? 0)}
								</span>
							</div>

							<div className="flex items-center justify-between border-t pt-2">
								<span className="font-semibold">คงเหลือ</span>
								<span className="text-2xl font-bold text-green-600">
									{formatNumber(creditQuotaRemaining)}
								</span>
							</div>
						</div>
					</div>
				</div>

				{/* Agent Info */}
				<div className="mt-4 flex items-center justify-between border-t pt-3 text-xs text-muted-foreground">
					<span>Agent: {data?.username}</span>
					<span>ID: {data?.agentId}</span>
				</div>
			</CardContent>
		</Card>
	)
}
